/**
 * Nationality on the room application form (COL-618): the country list the
 * picker shows, and the rule for who must upload a residence permit.
 *
 * Stored as an ISO 3166-1 alpha-2 code in `profiles.nationality`; the German (or
 * English, ...) name is only ever rendered, never stored, so adding a language
 * never touches the column.
 *
 * `NO_PERMIT_COUNTRY_CODES` mirrors supabase/functions/_shared/residence-permit.ts
 * — create-application-account enforces the same rule server-side. The two are
 * asserted equal by nationality.parity.test.ts.
 */

/** The 27 EU member states + Iceland, Liechtenstein, Norway, Switzerland. */
export const NO_PERMIT_COUNTRY_CODES: readonly string[] = [
  // EU
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA (non-EU) and Switzerland
  "IS",
  "LI",
  "NO",
  "CH",
];

/**
 * Every ISO 3166-1 alpha-2 code a person can hold the nationality of, plus
 * Kosovo (XK, user-assigned but on real passports). Left out: the uninhabited
 * territories — Antarctica, Bouvet Island, Heard & McDonald Islands, the French
 * Southern Territories, US Minor Outlying Islands, South Georgia and the
 * British Indian Ocean Territory.
 */
export const COUNTRY_CODES: readonly string[] = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "XK",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
];

/** The code the picker lists first, whatever the language. */
export const HOME_COUNTRY_CODE = "DE";

const COUNTRY_CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

/**
 * An upper-case code from the list the picker offers, or null. Membership, not
 * just shape: a well-formed but made-up code ("ZZ") would otherwise be stored as
 * a nationality and, being outside the EU list, demand a permit.
 */
export function normalizeNationality(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim().toUpperCase();
  return COUNTRY_CODE_SET.has(code) ? code : null;
}

/**
 * True when this nationality must come with a residence permit. A missing or
 * unrecognisable nationality requires nothing — the form separately insists a
 * nationality is chosen at all.
 */
export function requiresResidencePermit(nationality: unknown): boolean {
  const code = normalizeNationality(nationality);
  return code !== null && !NO_PERMIT_COUNTRY_CODES.includes(code);
}

function displayLocale(language: string): string {
  return language && language.length >= 2 ? language : "en";
}

const namers = new Map<string, Intl.DisplayNames>();

function namerFor(language: string): Intl.DisplayNames {
  const locale = displayLocale(language);
  let namer = namers.get(locale);
  if (!namer) {
    try {
      namer = new Intl.DisplayNames([locale, "en"], { type: "region" });
    } catch {
      namer = new Intl.DisplayNames(["en"], { type: "region" });
    }
    namers.set(locale, namer);
  }
  return namer;
}

/** The country's name in `language`, or the bare code when the runtime cannot name it. */
export function countryName(code: string, language: string): string {
  try {
    return namerFor(language).of(code) ?? code;
  } catch {
    return code;
  }
}

export interface CountryOption {
  code: string;
  name: string;
  /** English name, searchable even when the list is shown in another language. */
  englishName: string;
}

/**
 * Every selectable country, Deutschland first and the rest alphabetical in the
 * language's own collation (so "Österreich" sorts under O, not after Z).
 */
export function countryOptions(language: string): CountryOption[] {
  const collator = new Intl.Collator(displayLocale(language), {
    sensitivity: "base",
  });
  const options = COUNTRY_CODES.map((code) => ({
    code,
    name: countryName(code, language),
    englishName: countryName(code, "en"),
  }));
  const home = options.filter((o) => o.code === HOME_COUNTRY_CODE);
  const rest = options
    .filter((o) => o.code !== HOME_COUNTRY_CODE)
    .sort((a, b) => collator.compare(a.name, b.name));
  return [...home, ...rest];
}

function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * The countries a search shows, in the order the picker lists them: names that
 * start with the query first (the shown language, or English — so "syria" finds
 * Syrien), then names that merely contain it. Each group keeps the list order,
 * Deutschland first and the rest alphabetical, so "the first match" is always
 * the same country for the same query, whatever was typed before it.
 *
 * English names match by prefix only: matched anywhere, "land" would also pull
 * in every "... Islands" country.
 */
export function searchCountries(
  options: CountryOption[],
  query: string
): CountryOption[] {
  const q = fold(query);
  if (!q) return options;
  const startsWith: CountryOption[] = [];
  const contains: CountryOption[] = [];
  for (const option of options) {
    const name = fold(option.name);
    if (name.startsWith(q) || fold(option.englishName).startsWith(q)) {
      startsWith.push(option);
    } else if (name.includes(q)) {
      contains.push(option);
    }
  }
  return [...startsWith, ...contains];
}
